import React from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LazyIconWrapper } from "../ui/LazyIconWrapper";
import { PollPreview } from "../editor/PollPreview";
import PollCreatorComponent from "../PollCreator";
import { useEditorState } from "../editor/EditorStateProvider";
import {
  FormPollCreator,
  AvailabilityPollCreatorContent,
  QuizzCreate,
} from "../../exports";
import type { Poll } from "../../lib/pollStorage";

interface WorkspaceEditorProps {
  isMobile: boolean;
  showManualEditorOnMobile: boolean;
  setShowManualEditorOnMobile: (val: boolean) => void;
  isEditorOpen: boolean;
  closeEditor: () => void;
  currentPoll: Poll | null;
  pollTypeFromUrl: string;
  setMobileActiveTab: (tab: "chat" | "editor") => void;
  setPublishedPoll: (poll: Poll) => void;
}

export const WorkspaceEditor: React.FC<WorkspaceEditorProps> = ({
  isMobile,
  showManualEditorOnMobile,
  setShowManualEditorOnMobile,
  isEditorOpen,
  closeEditor,
  currentPoll,
  pollTypeFromUrl,
  setMobileActiveTab,
  setPublishedPoll,
}) => {
  const { getPersistedPollId } = useEditorState();
  const { t } = useTranslation("polls");
  return (
    <div className="relative h-full overflow-y-auto bg-background pt-4 px-4 pb-20">
      {/* The mobile close X used to live here; the bottom MobileNavigationTabs
          (chat / editor icons) now handles switching back to chat. */}
      {isEditorOpen && currentPoll ? (
        currentPoll.type === "date" ? (
          // Pour les sondages de dates, utiliser PollCreator avec les données du poll
          (() => {
            const initialData = {
              title: currentPoll.title,
              description: currentPoll.description,
              dates: currentPoll.dates ||
                (currentPoll as any).selectedDates ||
                (currentPoll as any).settings?.selectedDates || [
                  new Date().toISOString().split("T")[0], // Aujourd'hui
                  new Date(Date.now() + 86400000).toISOString().split("T")[0], // Demain
                ], // Fallback si l'IA ne trouve pas de dates
              dateGroups: currentPoll.dateGroups?.map((group) => ({
                ...group,
                type:
                  group.type === "week" || group.type === "fortnight"
                    ? ("range" as const)
                    : group.type,
              })),
              timeSlots:
                (currentPoll as any).timeSlots ||
                (currentPoll as any).settings?.timeSlots,
              timeSlotsByDate:
                (currentPoll as any).timeSlotsByDate ||
                (currentPoll as any).settings?.timeSlotsByDate,
              type: "date" as const,
              // Cadence/audience détectées par l'IA → pré-remplissage intent bar.
              cadence: (currentPoll as any).cadence,
              audience: (currentPoll as any).audience,
              // Marqueur posé uniquement par pollFromSuggestion (vraie génération
              // IA) — distingue d'un brouillon vide/magic-link qui a aussi un
              // titre par défaut mais ne doit PAS afficher le hint "pré-rempli".
              aiPrefilled: (currentPoll as any).aiPrefilled === true,
            };

            return (
              // ⚠️ CRITICAL: onFinalize est OBLIGATOIRE pour afficher l'écran de succès.
              // Sans ce callback, setPublishedPoll n'est jamais appelé et l'utilisateur
              // reste bloqué après la création du sondage. Ne PAS supprimer.
              <PollCreatorComponent
                key={`poll-creator-${currentPoll?.id || "new"}-${currentPoll ? "loaded" : "empty"}`}
                onBack={() => {
                  closeEditor();
                }}
                initialData={initialData}
                getPersistedPollId={getPersistedPollId}
                onFinalize={(_draft, savedPoll) => {
                  if (savedPoll) {
                    setPublishedPoll(savedPoll);
                  }
                }}
              />
            );
          })()
        ) : (
          // Pour les autres types (Form, Availability, Quizz), afficher la preview
          <>
            {/* Bouton fermer en absolute dans l'éditeur (Desktop) */}
            {!isMobile && (
              <button
                onClick={closeEditor}
                className="absolute top-0 right-0 z-50 p-2 bg-card hover:bg-accent text-muted-foreground hover:text-foreground rounded-lg transition-colors"
                aria-label={t("workspace_editor.close_editor_aria")}
                title={t("workspace_editor.close")}
                data-testid="aicreationworkspace-close-desktop"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            <PollPreview poll={currentPoll} />
          </>
        )
      ) : // Afficher le créateur vide par défaut selon le type
      pollTypeFromUrl === "form" ? (
        <FormPollCreator
          key={`form-creator-${currentPoll?.id || "new"}-${currentPoll ? "loaded" : "empty"}`}
          initialDraft={undefined}
          onCancel={() => {
            console.log("🔍 [WorkspaceEditor] FormPollCreator onCancel");
            setShowManualEditorOnMobile(false);
            setMobileActiveTab("chat");
          }}
          onSave={() => {}}
          onFinalize={(draft: any, savedPoll?: any) => {
            console.log("🔍 [WorkspaceEditor] FormPollCreator onFinalize", {
              draft: !!draft,
              savedPoll: !!savedPoll,
            });
            // FormPollCreator persiste en DB et renvoie le Poll sauvé (slug
            // réel) en 2ᵉ arg. Sans ça, publishedPoll garde l'id local
            // "draft-xxx" → lien partage /poll/draft-xxx/vote mort (Bug P0 29/05).
            const finalPoll = savedPoll || draft;
            if (finalPoll) {
              setPublishedPoll(finalPoll);
            }
            setShowManualEditorOnMobile(false);
          }}
        />
      ) : pollTypeFromUrl === "availability" ? (
        <AvailabilityPollCreatorContent
          onBack={(createdPoll) => {
            if (createdPoll) {
              setPublishedPoll(createdPoll);
            }
            setShowManualEditorOnMobile(false);
            setMobileActiveTab("chat");
          }}
          onCreate={() => {}}
        />
      ) : pollTypeFromUrl === "quizz" ? (
        <QuizzCreate
          onBack={() => {
            setShowManualEditorOnMobile(false);
            setMobileActiveTab("chat");
          }}
        />
      ) : (
        // ⚠️ CRITICAL: onFinalize est OBLIGATOIRE pour afficher l'écran de succès.
        // Sans ce callback, setPublishedPoll n'est jamais appelé et l'utilisateur
        // reste bloqué après la création du sondage. Ne PAS supprimer.
        // Régression introduite par commit 66319ab0 — corrigée ici.
        <PollCreatorComponent
          key={`poll-creator-${currentPoll?.id || "new"}-${currentPoll ? "loaded" : "empty"}`}
          onBack={() => {
            setShowManualEditorOnMobile(false);
            setMobileActiveTab("chat");
          }}
          initialData={undefined}
          onFinalize={(_draft, savedPoll) => {
            if (savedPoll) {
              setPublishedPoll(savedPoll);
            }
          }}
        />
      )}
    </div>
  );
};
